import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const cardBenefitVariants = cva('relative bg-white rounded-lg shadow-sm border p-6', {
  variants: {
    variant: {
      primary: 'border-primary-200 bg-primary-50/30',
      secondary: 'border-secondary-200 bg-secondary-50/30',
      accent: 'border-accent-200 bg-accent-50/30',
      success: 'border-success bg-success-lighter/30',
      warning: 'border-warning bg-warning/10',
      info: 'border-accent-200 bg-accent-50/30',
      default: 'border-gray-200 bg-gray-50/30',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const cardBenefitBadgeVariants = cva('absolute top-4 right-4 px-2 py-1 text-xs font-medium rounded-full', {
  variants: {
    variant: {
      primary: 'bg-primary-50 text-primary-dark',
      secondary: 'bg-secondary-50 text-secondary-dark',
      accent: 'bg-accent-50 text-accent-dark',
      success: 'bg-success-lighter text-success-dark',
      warning: 'bg-warning/10 text-warning',
      info: 'bg-accent-50 text-accent-dark',
      default: 'bg-gray-200 text-gray-700',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const bulletVariants = cva('flex-shrink-0 w-2 h-2 rounded-full mt-2', {
  variants: {
    variant: {
      primary: 'bg-primary',
      secondary: 'bg-secondary',
      accent: 'bg-accent',
      success: 'bg-success',
      warning: 'bg-warning',
      info: 'bg-accent',
      default: 'bg-gray-600',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const getBenefitLevel = (text: string): { level: number; cleanText: string; hasEmoji: boolean } => {
  const trimmed = text.trim();

  // Level 0: Main items with emojis (✨, 🔥, etc.)
  // Explicitly match common emoji and symbols like ✨, 🔥, etc.
  if (/^(?:[\u{1F300}-\u{1F9FF}]|[\u2600-\u26FF]|\u2728|\u{2728}|\u{1F525})/u.test(trimmed)) {
    return { level: 0, cleanText: trimmed, hasEmoji: true };
  }

  // Level 1: Items starting with •
  if (trimmed.startsWith('•')) {
    return { level: 1, cleanText: trimmed.substring(1).trim(), hasEmoji: false };
  }

  // Level 1: Items starting with spaces + •
  if (trimmed.startsWith(' •')) {
    return { level: 1, cleanText: trimmed.substring(2).trim(), hasEmoji: false };
  }

  // Default level 0
  return { level: 0, cleanText: trimmed, hasEmoji: false };
};

export interface CardBenefitProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof cardBenefitVariants> {
  when: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  benefits: React.ReactNode[];
}

const CardBenefit = React.forwardRef<HTMLDivElement, CardBenefitProps>(({ className, variant, when, title, subtitle, benefits, ...props }, ref) => {
  return (
    <div ref={ref} className={cn(cardBenefitVariants({ variant }), className)} {...props}>
      <span className={cardBenefitBadgeVariants({ variant })}>{when}</span>
      <div className="pr-16">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <div className="text-gray-600 mb-4">{subtitle}</div>
        <ul className="flex flex-col gap-2">
          {benefits.map((item, itemIndex) => {
            const itemString = typeof item === 'string' ? item : '';
            const { level, cleanText, hasEmoji } = getBenefitLevel(itemString);

            return (
              <li key={itemIndex} className={`flex items-start gap-3 ${level === 1 ? 'ml-4' : ''}`}>
                {!hasEmoji && <span className={bulletVariants({ variant })} />}
                <div className={`text-gray-700 text-sm ${hasEmoji ? 'font-medium' : ''}`}>
                  {typeof item === 'string' ? (hasEmoji ? item : cleanText) : item}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
});

CardBenefit.displayName = 'CardBenefit';

export { CardBenefit, cardBenefitVariants };
